/** Wall-clock ⇄ instant conversion for the UTC/local timezone toggle.
 *
 * date-fns has no timezone support and MUI's `timezone` prop doesn't work with
 * the date-fns adapter, so absolute-mode pickers are fed plain `Date`s whose
 * LOCAL fields carry the wall clock in the selected timezone; these helpers
 * convert between that representation and real instants, to the second (the panel
 * edits to the second; milliseconds are dropped).
 *
 * Known DST edges (accepted, ~1 h/year each):
 * - 'utc' mode: a UTC wall clock that happens to be a nonexistent LOCAL time
 *   (spring-forward gap) gets nudged by the JS engine — display-only skew.
 * - 'local' mode: an ambiguous fall-back wall clock resolves to whichever
 *   offset the JS engine picks.
 */
import type { TimeWindowTz } from './types';

/** Canonical wire format for datetime bounds (backend parse_datetime, UTC). */
export const NAIVE_UTC_FORMAT = 'yyyy-MM-dd HH:mm';

/** Instant → a Date whose local fields show the wall clock in `tz` (for
 *  feeding MUI pickers). */
export function instantToWallClock(instant: Date, tz: TimeWindowTz): Date {
  if (tz === 'local') return instant;
  return new Date(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate(),
    instant.getUTCHours(),
    instant.getUTCMinutes(),
    instant.getUTCSeconds(),
  );
}

/** Inverse: a picker-produced Date read as a wall clock in `tz` → instant. */
export function wallClockToInstant(wall: Date, tz: TimeWindowTz): Date {
  if (tz === 'local') return wall;
  return new Date(
    Date.UTC(wall.getFullYear(), wall.getMonth(), wall.getDate(), wall.getHours(), wall.getMinutes(), wall.getSeconds()),
  );
}

/** Instant → naive-UTC wire string. Built from toISOString digits so the
 *  result never depends on the browser timezone (same trick as
 *  features/delivery-logs/createdWindow.ts). */
export function instantToNaiveUtc(instant: Date): string {
  return instant.toISOString().slice(0, 16).replace('T', ' ');
}

const NAIVE_UTC_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;

/** Naive-UTC wire string → instant; null for anything else (garbage, legacy
 *  date-only bounds) so callers can fall back to an empty picker side. */
export function naiveUtcToInstant(value: string): Date | null {
  const m = NAIVE_UTC_RE.exec(value);
  if (!m) return null;
  const instant = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!));
  // Round-trip check rejects field rollover (month 13, Feb 30, hour 25, …).
  return instantToNaiveUtc(instant) === value ? instant : null;
}
